import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  doc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Edit, 
  Download, 
  Filter,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Printer,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { downloadBillPDF, viewBillPDF } from '../../utils/pdfGenerator';

export default function BillsManagement() {
  const { currentUser, userRole, isAdmin } = useAuth();
  const [bills, setBills] = useState([]);



  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);



  useEffect(() => {
    loadBills();
  }, []);

  const loadBills = async () => {
    try {

      const q = query(collection(db, 'bills'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const billsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBills(billsData);
    } catch (error) {
      console.error('Error loading bills:', error);
    }
  };

  const handleUpdatePayment = async (billId, paymentData) => {
    try {
      await updateDoc(doc(db, 'bills', billId), {
        ...paymentData,
        updatedAt: new Date()
      });
      loadBills();
      setShowPaymentModal(false);
      setSelectedBill(null);
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment status');
    }
  };

  const handleDeleteBill = async (billId) => {
    // Check if user is admin
    if (!isAdmin) {
      alert('Only administrators can delete bills.');
      return;
    }

    if (window.confirm('⚠️ Are you sure you want to delete this bill? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'bills', billId));
        loadBills();
        alert('✅ Bill deleted successfully.');
      } catch (error) {
        console.error('Error deleting bill:', error);
        alert('❌ Failed to delete bill. Please try again.');
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'overdue': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = bill.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         bill.customerPhone?.includes(searchTerm) ||
                         bill.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || bill.status === filterStatus;
    return matchesSearch && matchesStatus;
  });





  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bills Management</h1>
          <p className="mt-2 text-gray-600">Manage and track all bills and payments</p>
        </div>
        <button
          onClick={() => window.location.href = '/billing/create'}
          className="btn-primary flex items-center"
        >
          <FileText className="h-4 w-4 mr-2" />
          Create New Bill
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by customer name, phone, or email..."
            className="input-field pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="input-field"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
        <div className="flex justify-end">
          <button className="btn-secondary flex items-center">
            <Filter className="h-4 w-4 mr-2" />
            Export All
          </button>
        </div>
      </div>

      {/* Bills Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bill #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredBills.map((bill) => (
              <tr key={bill.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    #{bill.id.substring(0, 8)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {bill.customerName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {bill.customerPhone}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {bill.items?.length || 0} item{bill.items?.length !== 1 ? 's' : ''}
                  </div>
                  <div className="text-sm text-gray-500">
                    {bill.items?.slice(0, 2).map(item => item.name).join(', ')}
                    {bill.items?.length > 2 && ` +${bill.items.length - 2} more`}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    ₹{bill.total?.toLocaleString()}
                  </div>
                  {bill.paymentMethod && (
                    <div className="text-sm text-gray-500 capitalize">
                      {bill.paymentMethod.replace('_', ' ')}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {getStatusIcon(bill.status)}
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}>
                      {bill.status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {bill.createdAt?.toDate ? 
                    format(bill.createdAt.toDate(), 'MMM dd, yyyy') : 
                    'Unknown'
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">

                    <button
                      onClick={() => {
                        console.log('View PDF button clicked for bill:', bill.id, bill);
                        viewBillPDF(bill, bill.id);
                      }}
                      className="p-2 text-gray-400 hover:text-green-600"
                      title="View PDF"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        console.log('Download PDF button clicked for bill:', bill.id, bill);
                        downloadBillPDF(bill, bill.id);
                      }}
                      className="p-2 text-gray-400 hover:text-purple-600"
                      title="Download PDF"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedBill(bill);
                        setShowPaymentModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Update Payment"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    {/* Delete button - Only visible to admin users */}
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteBill(bill.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete Bill (Admin Only)"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredBills.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No bills found.</p>
          </div>
        )}
      </div>



      {/* Payment Update Modal */}
      {showPaymentModal && selectedBill && (
        <PaymentUpdateModal
          bill={selectedBill}
          onUpdate={handleUpdatePayment}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
          }}
        />
      )}
    </div>
  );
}



// Payment Update Modal Component
function PaymentUpdateModal({ bill, onUpdate, onClose }) {
  const [paymentData, setPaymentData] = useState({
    status: bill.status || 'pending',
    paymentMethod: bill.paymentMethod || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(bill.id, paymentData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Update Payment Status
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Status
              </label>
              <select
                className="input-field"
                value={paymentData.status}
                onChange={(e) => setPaymentData({ ...paymentData, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              <select
                className="input-field"
                value={paymentData.paymentMethod}
                onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
              >
                <option value="">Select payment method</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
